import React, { useState, useCallback, useContext } from 'react';
import { Button, LinearProgress, Alert } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import { styled } from '@mui/material/styles';
import { ProjectContext } from "../../context/project_context";

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ProjectGrabAudio = () => {
  const { saveAudioUrlToProject, project, updateProject } = useContext(ProjectContext);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/aac'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|ogg|m4a|aac)$/i)) {
      setUploadError('Please select a valid audio file (MP3, WAV, OGG, M4A, or AAC)');
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      // Get presigned URL from backend
      const response = await fetch('/api/s3/presigned_url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type || 'audio/mpeg',
          folder: 'audio', // Store audio files in 'audio' folder
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { url, fields } = await response.json();

      // Create form data for upload
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      // Upload to S3
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            setUploadProgress(percentComplete);
          }
        });

        xhr.addEventListener('load', async () => {
          if (xhr.status === 201 || xhr.status === 204) {
            const finalUrl = `${url}/${fields.key}`;
            setUploadedUrl(finalUrl);
            await saveAudioUrlToProject(finalUrl);
            resolve();
          } else {
            reject(new Error('Upload failed'));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        xhr.open('POST', url, true);
        xhr.send(formData);
      });

    } catch (error) {
      console.error('Error:', error);
      setUploadError(error.message);
    } finally {
      setIsUploading(false);
    }
  }, [saveAudioUrlToProject]);

  const handleSkip = async () => {
    // Allow skipping to transcribe_audio regardless of media upload status
    // User can upload SRT file directly in the transcribe step if needed
    await updateProject({
      ...project,
      project_status: "transcribe_audio"
    });
  };

  const handleBack = async () => {
    await updateProject({
      ...project,
      project_status: "split_video"
    });
  };

  return (
    <div className="project-grab-audio">
      <h2>Grab Audio</h2>
      <div className="audio-content">
        <p className="mb-3">Project Name: <strong>{project.name}</strong></p>
        
        {/* Show existing media info */}
        {(project.video_url || project.audio_url) && (
          <div className="mb-4 p-3" style={{ 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <h6 className="mb-2">Current Media:</h6>
            {project.video_url && (
              <div className="mb-2">
                <span className="badge bg-primary me-2">Video</span>
                <small className="text-muted text-truncate" style={{ maxWidth: '300px', display: 'inline-block', verticalAlign: 'middle' }}>
                  {project.video_url}
                </small>
              </div>
            )}
            {project.audio_url && (
              <div>
                <span className="badge bg-success me-2">Audio</span>
                <small className="text-muted text-truncate" style={{ maxWidth: '300px', display: 'inline-block', verticalAlign: 'middle' }}>
                  {project.audio_url}
                </small>
              </div>
            )}
          </div>
        )}

        <div className="mb-4">
          <p className="text-muted mb-3">
            Upload an audio file (MP3, WAV, OGG, M4A, or AAC) for transcription, 
            or skip to the next step to upload an SRT file directly.
          </p>
          
          <div className="d-flex gap-2 flex-wrap">
            <Button
              component="label"
              variant="contained"
              startIcon={<AudioFileIcon />}
              disabled={isUploading}
              sx={{ 
                backgroundColor: '#28a745',
                '&:hover': { backgroundColor: '#218838' }
              }}
            >
              {isUploading ? 'Uploading...' : 'Upload Audio File'}
              <VisuallyHiddenInput
                type="file"
                accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac"
                onChange={handleFileSelect}
              />
            </Button>
          </div>
        </div>

        {/* Upload progress */}
        {isUploading && (
          <div className="mb-3">
            <LinearProgress 
              variant="determinate" 
              value={uploadProgress} 
              sx={{ height: 8, borderRadius: 4 }}
            />
            <small className="text-muted mt-1 d-block">
              Uploading... {Math.round(uploadProgress)}%
            </small>
          </div>
        )}

        {/* Error message */}
        {uploadError && (
          <Alert severity="error" className="mb-3" onClose={() => setUploadError(null)}>
            {uploadError}
          </Alert>
        )}

        {/* Success message with audio preview */}
        {uploadedUrl && (
          <Alert severity="success" className="mb-3">
            <div className="mb-2">Audio uploaded successfully!</div>
            <audio controls src={uploadedUrl} style={{ width: '100%', maxWidth: '400px' }} />
          </Alert>
        )}

        {/* Navigation buttons */}
        <div className="d-flex gap-2 mt-4">
          <button 
            className="btn btn-secondary"
            onClick={handleBack}
          >
            Back to Split Video
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleSkip}
          >
            Skip to Transcribe Audio
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectGrabAudio;
